import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Pencil, Check, Copy, Clock } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';
import { TraitStyleConfig, AttributeStyleConfig, IconStyleId, DeviceModalConfig } from '../../types';

export interface ButtonDevice {
  deviceId: string;
  baseSN?: string;
  deviceName?: string;
  nickname?: string;
  location?: string;
  groupName?: string;
  isOnline?: boolean;
  lastPressed?: string | Date;
  pressType?: 'click' | 'doubleClick' | 'longPress' | string;
  traits?: string[];
  attributes?: Record<string, unknown>;
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
}

export interface ButtonModalProps {
  device: ButtonDevice;
  onClose: () => void;
  onSaveNickname?: (device: ButtonDevice, nickname: string | null) => Promise<void>;
  config?: Partial<DeviceModalConfig>;
  isMergedDevice?: boolean;
  onToggleMerge?: (baseSN: string) => void;
  // 24H Event history chart data (uses occupancyChartData for consistency with DeviceTemplateProps)
  occupancyChartData?: Array<{ time: string; value: number; label?: string }>;
  isLoadingOccupancyChart?: boolean;
}

/**
 * Button Modal Template
 * 
 * 按鈕/場景觸發器顯示 Modal (只讀，無控制)
 * - 最後按壓時間
 * - 按壓類型 (單擊/雙擊/長按)
 */
export const Template01_Status: React.FC<ButtonModalProps> = ({
  device,
  onClose,
  onSaveNickname,
  config,
  isMergedDevice = false,
  onToggleMerge,
  occupancyChartData = [],
  isLoadingOccupancyChart = false,
}) => {
  // Nickname editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(device.nickname || device.deviceName || '');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];

  // Sync editedName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && !isSavingRef.current && Date.now() > blockSyncUntilRef.current) {
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  // Device state
  const displayName = device.nickname || device.deviceName || device.deviceId;
  const displaySN = isMergedDevice ? device.baseSN : device.deviceId;

  // Multi-channel check
  const isMultiChannel = device.system?.endpointRelationship?.classification === 'multi_channel';

  // Format last pressed time
  const formatLastPressed = (date: string | Date | undefined): string => {
    if (!date) return '尚未按壓';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('zh-TW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get press type label
  const getPressTypeLabel = (type: string | undefined): string => {
    switch (type) {
      case 'click': return '單擊';
      case 'doubleClick': return '雙擊';
      case 'longPress': return '長按';
      default: return type || '未知';
    }
  };

  // Handle nickname save
  const handleSaveNickname = useCallback(async () => {
    if (onSaveNickname && editedName !== (device.nickname || device.deviceName)) {
      isSavingRef.current = true;
      blockSyncUntilRef.current = Date.now() + 3000;
      await onSaveNickname(device, editedName || null);
      isSavingRef.current = false;
    }
    setIsEditingName(false);
  }, [device, editedName, onSaveNickname]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [handleSaveNickname, device.nickname, device.deviceName]);

  // Copy SN to clipboard
  const handleCopySN = useCallback(() => {
    navigator.clipboard.writeText(displaySN || '');
  }, [displaySN]);

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="button" 
              isOn={false}
              size={20}
              styleId={smallIconStyle}
            />
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="bg-slate-800 text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">{displayName}</span>
            )}
            {isEditingName ? (
              <button onClick={handleSaveNickname} className="text-purple-400 hover:text-purple-300">
                <Check className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.isOnline,
                label: device.isOnline ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Button visual */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-8">
              <div className="w-32 h-32 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50 shadow-lg shadow-purple-500/20">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-inner">
                  <DeviceIcon
                    deviceType="button"
                    isOn={true}
                    size={36}
                    styleId={bigIconStyle}
                    className="text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Press info */}
          <div className="space-y-3">
            {/* Last pressed */}
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">最後按壓</span>
              </div>
              <div className="text-lg text-white">
                {formatLastPressed(device.lastPressed)}
              </div>
            </div>

            {/* Press type (if available) */}
            {device.pressType && (
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined w-4 h-4 text-purple-400" style={{ fontSize: '16px' }}>
                    gesture
                  </span>
                  <span className="text-sm text-slate-400">按壓類型</span>
                </div>
                <div className="text-lg text-purple-400 font-medium">
                  {getPressTypeLabel(device.pressType)}
                </div>
              </div>
            )}
          </div>

          {/* 24H Event Chart */}
          {!hiddenComponents.includes('SensorChart') && (
            <DynamicAttribute
              attributeName="SensorChart"
              styleId={getAttributeStyleId('SensorChart', attributeStyles)}
              componentProps={{
                data: occupancyChartData || [],
                dataKey: "value",
                loading: isLoadingOccupancyChart,
                title: "過去 24 小時按壓記錄",
                color: "#a855f7",
                unit: " 次",
                emptyMessage: "暫無按壓記錄",
                height: 120
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-800/50 space-y-3">
          {/* Multi-channel toggle */}
          {isMultiChannel && onToggleMerge && device.baseSN && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  isMergedDevice 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                單一方塊
              </button>
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  !isMergedDevice 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                個別方塊
              </button>
            </div>
          )}

          {/* Location and SN */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{displaySN}</span>
              <button onClick={handleCopySN} className="text-slate-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Status;
